import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { TrendingUp, Users, CreditCard, AlertCircle, Calendar, Trophy, Target, Clock } from 'lucide-react';
import LeaderboardModal from '../components/LeaderboardModal';

function formatCurrency(n) {
  if (!n) return '₹0';
  return '₹' + Number(n).toLocaleString('en-IN');
}

function StatCard({ title, value, sub, icon: Icon, variant, color }) {
  return (
    <div className={`stat-card ${variant || ''}`} style={color ? { borderTop: `3px solid ${color}` } : {}}>
      <div className="flex-between mb-8">
        <div className="card-title">{title}</div>
        {Icon && <Icon size={16} style={{ color: 'var(--text-light)' }} />}
      </div>
      <div className="card-value">{value}</div>
      {sub && <div className="card-sub">{sub}</div>}
    </div>
  );
}

function LeaderboardRow({ rank, name, total, target, achievement, isTop }) {
  const pct = Math.min(achievement, 100);
  const color = pct >= 100 ? 'var(--success)' : pct >= 70 ? 'var(--warning)' : 'var(--danger)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ width: 24, height: 24, borderRadius: '50%', background: rank === 1 ? 'var(--gold)' : 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: rank === 1 ? 'var(--navy)' : 'var(--text-muted)', flexShrink: 0 }}>
        {rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : rank}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
        <div className="progress-bar mt-4">
          <div className="progress-fill" style={{ width: `${pct}%`, background: color }} />
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>{formatCurrency(total)}</div>
        <div style={{ fontSize: 11, color, fontWeight: 600 }}>{achievement}%</div>
      </div>
    </div>
  );
}

function fmt(n) { return '₹' + (parseInt(n) || 0).toLocaleString('en-IN'); }

function ProposalsSentModal({ onClose, onNavigate }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('today');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/proposals-sent').then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const leads = data?.[filter]?.leads || [];

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 700, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div className="modal-title">📄 Proposals Sent</div>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: '0 24px 12px', display: 'flex', gap: 8 }}>
          {[
            { key: 'today', label: `Today (${data?.today?.count || 0})` },
            { key: 'weekly', label: `This Week (${data?.weekly?.count || 0})` },
            { key: 'monthly', label: `This Month (${data?.monthly?.count || 0})` },
          ].map(t => (
            <button key={t.key} className={`btn btn-sm ${filter === t.key ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilter(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ overflowY: 'auto', padding: '0 24px 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : leads.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>No proposals sent {filter}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--surface)' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Client</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Consultant</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Sent On</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Fee</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((p, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => { onClose(); onNavigate?.('leads'); }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>{p.clientName}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{p.consultantName}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-muted)', fontSize: 12 }}>{new Date(p.proposalSharedAt).toLocaleDateString('en-IN')}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: 'var(--success)' }}>₹{(p.fee || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {p.isEnrolled
                        ? <span className="badge badge-green">✓ Enrolled</span>
                        : <span className="badge badge-blue">Proposal Sent</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function PromisesModal({ onClose, onNavigate }) {
  const [promises, setPromises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    api.get('/dashboard/promises').then(r => setPromises(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'all' ? promises
    : filter === 'today' ? promises.filter(p => p.promiseDate === today)
    : filter === 'overdue' ? promises.filter(p => p.promiseDate < today && !p.promiseFulfilled)
    : promises.filter(p => p.promiseDate > today);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 700, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div className="modal-title">🤝 Promise Tracker</div>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: '0 24px 12px', display: 'flex', gap: 8 }}>
          {[
            { key: 'all', label: `All (${promises.length})` },
            { key: 'overdue', label: `🔴 Overdue (${promises.filter(p => p.promiseDate < today && !p.promiseFulfilled).length})` },
            { key: 'today', label: `📅 Today (${promises.filter(p => p.promiseDate === today).length})` },
            { key: 'upcoming', label: `⏰ Upcoming` },
          ].map(t => (
            <button key={t.key} className={`btn btn-sm ${filter === t.key ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setFilter(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ overflowY: 'auto', padding: '0 24px 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No promises found</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--surface)' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Client</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Consultant</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Type</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Promise Date</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Amount</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, i) => {
                  const isOverdue = p.promiseDate < today && !p.promiseFulfilled;
                  const isToday = p.promiseDate === today;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)', background: isOverdue ? '#fef2f2' : isToday ? '#fffbeb' : 'white', cursor: 'pointer' }}
                      onClick={() => { onClose(); onNavigate?.('leads'); }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600 }}>{p.clientName}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{p.consultantName}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span className={`badge ${p.isEnrolled ? 'badge-green' : 'badge-blue'}`} style={{ fontSize: 10 }}>
                          {p.isEnrolled ? '✓ Enrolled' : '📋 Lead'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: isOverdue ? 'var(--danger)' : isToday ? 'var(--warning)' : 'var(--text-main)', fontWeight: isOverdue || isToday ? 600 : 400 }}>
                        {new Date(p.promiseDate).toLocaleDateString('en-IN')}
                        {isOverdue && ' 🔴'}
                        {isToday && ' 📅'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: 'var(--success)' }}>{fmt(p.promiseAmount)}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {p.promiseFulfilled
                          ? <span className="badge badge-green">✓ Fulfilled</span>
                          : isOverdue ? <span className="badge badge-red">Overdue</span>
                          : isToday ? <span className="badge badge-yellow">Due Today</span>
                          : <span className="badge badge-blue">Upcoming</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--navy)' }}>
                  <td colSpan={4} style={{ padding: '10px 12px', color: 'var(--gold-light)', fontWeight: 700 }}>Total</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--gold-light)', fontWeight: 800 }}>{fmt(filtered.reduce((s, p) => s + (parseInt(p.promiseAmount) || 0), 0))}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard({ onNavigate }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPromises, setShowPromises] = useState(false);
  const [showProposals, setShowProposals] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [promises, setPromises] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    api.get('/dashboard').then(r => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}><div className="spinner" /></div>;
  if (!stats) return null;

  const isConsultant = user?.role === 'consultant';
  const deficitColor = stats.deficit > 0 ? 'var(--danger)' : 'var(--success)';

  return (
    <div className="page">
      {/* Collection overview */}
      <div style={{ marginBottom: 24 }}>
        <div className="flex-between mb-16">
          <h2 style={{ fontSize: 18 }}>Dashboard</h2>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
        </div>

        <div className="grid grid-4" style={{ marginBottom: 16 }}>
          <StatCard title="Today's Collection" value={formatCurrency(stats.todayCollection)} icon={CreditCard} color="#10b981" />
          <StatCard title="Monthly Collection" value={formatCurrency(stats.monthCollection)} sub={`Enroll: ${formatCurrency(stats.enrollmentCollection)} | Retainer: ${formatCurrency(stats.retainerCollection)}`} icon={TrendingUp} color="#3b82f6" />
          <StatCard title="Active Leads" value={stats.activeLeads} sub={`${stats.hotLeads} hot leads`} icon={Users} color="#f59e0b" />
          <StatCard title="Conversion Rate" value={`${stats.conversionRate}%`} sub={`${stats.enrolled} enrolled / ${stats.totalLeads} total`} icon={Target} color="#8b5cf6" />
        </div>

        <div className="grid grid-4">
          <StatCard title="New Leads" value={stats.newLeads} />
          <StatCard title="Credit Report Pending" value={stats.creditPending} color={stats.creditPending > 0 ? '#f59e0b' : undefined} />
          <StatCard title="Payment Expected" value={stats.paymentExpected} color={stats.paymentExpected > 0 ? '#3b82f6' : undefined} />
          <StatCard title="Active Clients" value={stats.activeClients} color="#10b981" />
        </div>
      </div>

      {/* Employee personal performance (all roles) */}
      {isConsultant && (
        <div className="card mb-24">
          <div className="flex-between mb-16">
            <h3 style={{ fontSize: 15 }}>My Monthly Performance</h3>
            <span className="badge badge-navy">Target: {formatCurrency(stats.myTarget)}</span>
          </div>
          <div className="grid grid-4">
            <StatCard title="Collected" value={formatCurrency(stats.myTotal)} sub={`Enroll: ${formatCurrency(stats.myEnrollCollection)}`} color="var(--success)" />
            <StatCard title="Target Till Today" value={formatCurrency(stats.targetSoFar)} />
            <StatCard title={stats.deficit > 0 ? 'Deficit' : 'Surplus'} value={formatCurrency(Math.abs(stats.deficit))} color={deficitColor} />
            <StatCard title="Daily Required" value={formatCurrency(stats.dailyRequired)} sub="to hit target" />
          </div>
          <div className="progress-bar mt-16" style={{ height: 10 }}>
            <div className="progress-fill" style={{ width: `${Math.min((stats.myTotal / stats.myTarget) * 100, 100)}%`, background: 'var(--navy)' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
            <span>{Math.round((stats.myTotal / stats.myTarget) * 100)}% achieved</span>
            <span>Target: {formatCurrency(stats.myTarget)}</span>
          </div>
        </div>
      )}

      <div className="grid" style={{ gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>
        {/* Action items */}
        <div>
          <div className="grid grid-2" style={{ marginBottom: 16 }}>
            <div className="card" style={{ cursor: 'pointer', borderLeft: '4px solid var(--warning)' }} onClick={() => onNavigate('followups')}>
              <div className="flex-center gap-8 mb-8">
                <Clock size={16} style={{ color: 'var(--warning)' }} />
                <span className="card-title">Follow-ups Today</span>
              </div>
              <div className="card-value" style={{ color: 'var(--warning)' }}>{stats.todayFollowUps}</div>
              {stats.overdueFollowUps > 0 && <div className="text-danger text-sm mt-4">{stats.overdueFollowUps} overdue</div>}
            </div>

            <div className="card" style={{ borderLeft: '4px solid var(--accent)', cursor: 'pointer' }} onClick={() => setShowPromises(true)}>
              <div className="flex-center gap-8 mb-8">
                <Calendar size={16} style={{ color: 'var(--accent)' }} />
                <span className="card-title">Promises Today</span>
              </div>
              <div className="card-value" style={{ color: 'var(--accent)' }}>{stats.todayPromises}</div>
              <div className="card-sub">{formatCurrency(stats.todayPromiseAmount)} expected</div>
              {stats.overduePromises > 0 && <div className="text-danger text-sm mt-4">{stats.overduePromises} overdue</div>}
              <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 6 }}>Click to view details →</div>
            </div>

            <div className="card" style={{ borderLeft: '4px solid #f59e0b', cursor: 'pointer' }} onClick={() => setShowLeaderboard(true)}>
              <div className="flex-center gap-8 mb-8">
                <Trophy size={16} style={{ color: '#f59e0b' }} />
                <span className="card-title">Team Leaderboard</span>
              </div>
              <div className="card-value" style={{ color: '#f59e0b' }}>🏆</div>
              <div className="card-sub">Performance rankings</div>
              <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 6 }}>Click to view →</div>
            </div>

            <div className="card" style={{ borderLeft: '4px solid #8b5cf6', cursor: 'pointer' }} onClick={() => setShowProposals(true)}>
              <div className="flex-center gap-8 mb-8">
                <TrendingUp size={16} style={{ color: '#8b5cf6' }} />
                <span className="card-title">Proposals Sent</span>
              </div>
              <div className="card-value" style={{ color: '#8b5cf6' }}>{stats.proposalsSentToday || 0}</div>
              <div className="card-sub">Today · {stats.proposalsSentWeek || 0} this week</div>
              <div style={{ fontSize: 11, color: '#8b5cf6', marginTop: 6 }}>Click to view details →</div>
            </div>
          </div>

          {/* Stage overview */}
          {['admin','tl'].includes(user?.role) && (
            <div className="card">
              <h3 style={{ fontSize: 14, marginBottom: 16 }}>Pipeline Overview</h3>
              {[
                { label: 'New Lead', count: stats.newLeads, color: '#64748b' },
                { label: 'Credit Report Pending', count: stats.creditPending, color: '#f59e0b' },
                { label: 'Payment Expected', count: stats.paymentExpected, color: '#3b82f6' },
                { label: 'Active Clients', count: stats.activeClients, color: '#10b981' },
              ].map(s => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: 13 }}>{s.label}</div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{s.count}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Leaderboard */}
        {['admin','tl'].includes(user?.role) && stats.leaderboard?.length > 0 && (
          <div className="card">
            <div className="flex-center gap-8 mb-16">
              <Trophy size={16} style={{ color: 'var(--gold)' }} />
              <h3 style={{ fontSize: 14 }}>Monthly Leaderboard</h3>
            </div>
            {stats.leaderboard.map((emp, i) => (
              <LeaderboardRow key={emp.id} rank={i+1} name={emp.name} total={emp.totalCollection} target={emp.target} achievement={emp.achievement} />
            ))}
          </div>
        )}
      </div>

      {/* Promises Modal */}
      {showPromises && <PromisesModal onClose={() => setShowPromises(false)} onNavigate={onNavigate} />}
      {showProposals && <ProposalsSentModal onClose={() => setShowProposals(false)} onNavigate={onNavigate} />}
      {showLeaderboard && <LeaderboardModal onClose={() => setShowLeaderboard(false)} />}
    </div>
  );
}
